// A journal's posts as an editorial board (PostBoard on the Mac): a column per Status in workflow order, then "No status"
// when a post has none. Ideas and drafts keep the contents' order (drag a card before another to reorder, within its
// folder); Scheduled runs soonest first, Published newest first, the newest 12 until "Show all". Dropping a card on a
// column sets its Status (scheduled or published, it takes its number then; published without a date, today's); dropped
// on Scheduled without a date, a date is asked for (No Date leaves it undated, "No date" on its card). A card opens the
// post's plan; its menu has Open in Write, New Section, Status and Label.
import { useRef, useState, type DragEvent } from 'react';
import { fragmentTags, JournalPosts, journalPostsInTree, type JournalPostRef, type MetadataValue, type OpenedProject, type Project, type TreeNode } from '@writing-suite/core';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { followLink, navigate } from '../../shell/route.ts';
import { structureEdits, useWorkspace } from '../../shell/workspace.ts';
import { TagChip } from '../FieldEditor.tsx';
import { ConfirmDialog, MenuPopup } from '../Menu.tsx';
import { formatNumber, pathTo } from '../helpers.ts';
import { dropPlacement } from '../structure.ts';
import { planItemMenu } from './menus.ts';

/** Published posts shown before "Show all". */
export const PUBLISHED_SHOWN = 12;

export interface PostColumn {
  /** The status option's id; '' for no status. */
  id: string;
  name: string;
  posts: JournalPostRef[];
}

const dateOf = (project: Project, post: JournalPostRef): Date | null => {
  const v = project.fragment(post.fragmentId)?.metadata['publish-date'];
  return v?.type === 'date' ? v.value : null;
};

/** `PostBoard.columns`: the posts by status, in the board's order (the phone's list uses the same). */
export function postColumns(project: Project, posts: JournalPostRef[]): PostColumn[] {
  const options = project.fields().find((f) => f.id === 'status')?.options ?? [];
  const statusOf = (p: JournalPostRef) => {
    const v = project.fragment(p.fragmentId)?.metadata.status;
    return v?.type === 'text' && options.some((o) => o.id === v.value) ? v.value : '';
  };
  const columns: PostColumn[] = options.map((o) => ({ id: o.id, name: o.name, posts: [] }));
  const none: JournalPostRef[] = [];
  for (const p of posts) {
    const s = statusOf(p);
    if (s) columns.find((c) => c.id === s)!.posts.push(p);
    else none.push(p);
  }
  if (none.length) columns.push({ id: '', name: 'No status', posts: none });
  // Scheduled soonest first (undated last); Published newest first (undated last); the rest as the contents have them.
  const time = (p: JournalPostRef, missing: number) => dateOf(project, p)?.getTime() ?? missing;
  for (const c of columns) {
    if (c.id === JournalPosts.scheduled) c.posts = c.posts.map((p, i) => [p, i] as const).sort((a, b) => time(a[0], Infinity) - time(b[0], Infinity) || a[1] - b[1]).map(([p]) => p);
    if (c.id === JournalPosts.published) c.posts = c.posts.map((p, i) => [p, i] as const).sort((a, b) => time(b[0], -Infinity) - time(a[0], -Infinity) || a[1] - b[1]).map(([p]) => p);
  }
  return columns;
}

/** The day after the latest scheduled post, else tomorrow (never today or before), as `yyyy-mm-dd`. */
export function suggestedDate(project: Project, posts: JournalPostRef[], except: string): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const tomorrow = day(new Date());
  tomorrow.setDate(tomorrow.getDate() + 1);
  const latest = posts
    .filter((p) => p.nodeId !== except && project.fragment(p.fragmentId)?.metadata.status?.type === 'text'
      && (project.fragment(p.fragmentId)!.metadata.status as { value: string }).value === JournalPosts.scheduled)
    .map((p) => dateOf(project, p)).filter((d): d is Date => !!d)
    .reduce<Date | null>((a, b) => (!a || b > a ? b : a), null);
  const next = latest ? day(latest) : null;
  if (next) next.setDate(next.getDate() + 1);
  const chosen = next && next > tomorrow ? next : tomorrow;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${chosen.getFullYear()}-${pad(chosen.getMonth() + 1)}-${pad(chosen.getDate())}`;
}

/** "Tue 14 Oct". */
export const dayName = (d: Date) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

function readShowAll(): boolean {
  try { return sessionStorage.getItem('ws:boardShowsAllPublished') === '1'; } catch { return false; }
}

export function PostBoard({ project, tree, hrefFor }: PageProps) {
  const ws = useWorkspace();
  const editable = !!ws?.canStructure;
  const posts = journalPostsInTree(tree);
  const columns = postColumns(project, posts);
  const [showAll, setShowAll] = useState(readShowAll);
  const [dating, setDating] = useState<{ post: JournalPostRef; day: string } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const dragging = useRef<JournalPostRef | null>(null);
  const columnOf = (p: JournalPostRef) => columns.find((c) => c.posts.includes(p))?.id ?? null;
  const node = (p: JournalPostRef): TreeNode | null => pathTo(tree, p.nodeId)?.at(-1) ?? null;

  const toggleShowAll = () => {
    setShowAll(!showAll);
    try { sessionStorage.setItem('ws:boardShowsAllPublished', showAll ? '0' : '1'); } catch { /* per session only */ }
  };

  /** A post dropped on a column takes its Status; on Scheduled without a date, the date is asked for. */
  const setStatus = (p: JournalPostRef, column: PostColumn) => {
    if (!ws) return;
    const value: MetadataValue | null = column.id ? { type: 'text', value: column.id } : null;
    structureEdits(ws).setField([p.nodeId], 'status', value);
    if (column.id === JournalPosts.scheduled && !dateOf(project, p)) setDating({ post: p, day: suggestedDate(project, posts, p.nodeId) });
  };

  /** Dropped on a card: another column sets Status; its own undated column moves it before or after the card in the
   *  contents (posts in the same folder only). */
  const dropOnCard = (target: JournalPostRef, column: PostColumn, side: 'before' | 'after') => {
    const moving = dragging.current;
    if (!moving || !ws || moving.nodeId === target.nodeId) return;
    if (columnOf(moving) !== column.id) return setStatus(moving, column);
    if (column.id === JournalPosts.scheduled || column.id === JournalPosts.published || moving.parentId !== target.parentId) return;
    const placement = dropPlacement(ws.fullTree ?? tree, [moving.nodeId], target.nodeId, side);
    if (placement) structureEdits(ws).move([moving.nodeId], placement);
  };

  return (
    <div className="corkboard-page post-board-page" data-layout="board">
      <div className="post-board" role="list" aria-label="Posts by status">
        {columns.map((column) => {
          const published = column.id === JournalPosts.published;
          const shown = published && !showAll ? column.posts.slice(0, PUBLISHED_SHOWN) : column.posts;
          return (
            <section
              key={column.id || 'none'}
              role="listitem"
              className={`post-column${over === column.id ? ' drop-target' : ''}`}
              data-status={column.id || 'none'}
              aria-label={`${column.name}, ${column.posts.length === 1 ? '1 post' : `${column.posts.length} posts`}`}
              onDragOver={(e) => {
                if (!dragging.current || !editable) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (over !== column.id) setOver(column.id);
              }}
              onDragLeave={(e) => {
                if (over === column.id && !(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
              }}
              onDrop={(e) => {
                const moving = dragging.current;
                dragging.current = null;
                setOver(null);
                if (!moving || !editable) return;
                e.preventDefault();
                if (columnOf(moving) !== column.id) setStatus(moving, column);
              }}
            >
              <h2 className="post-column-title">
                <span>{column.name}</span>
                <span className="post-column-count">{formatNumber(column.posts.length)}</span>
              </h2>
              <ul className="cards column-cards">
                {shown.map((p) => {
                  const n = node(p);
                  if (!n) return null;
                  return (
                    <PostCard
                      key={p.nodeId}
                      project={project}
                      post={p}
                      node={n}
                      tree={tree}
                      status={column.id}
                      href={hrefFor('plan/corkboard', p.nodeId)}
                      draggable={editable}
                      onDragStart={() => (dragging.current = p)}
                      onDragEnd={() => {
                        dragging.current = null;
                        setOver(null);
                      }}
                      onDropOn={(side) => dropOnCard(p, column, side)}
                      onMenu={(x, y) => setMenu({ x, y, id: p.nodeId })}
                    />
                  );
                })}
              </ul>
              {published && column.posts.length > PUBLISHED_SHOWN ? (
                <button type="button" className="link-button" onClick={toggleShowAll}>
                  {showAll ? `Show the newest ${PUBLISHED_SHOWN}` : `Show all ${formatNumber(column.posts.length)}`}
                </button>
              ) : null}
            </section>
          );
        })}
      </div>
      {menu && ws ? (
        <MenuPopup
          label="Post"
          at={menu}
          entries={[
            { label: 'Open in Plan', onSelect: () => navigate(hrefFor('plan/corkboard', menu.id)) },
            { label: 'Open in Write', onSelect: () => navigate(hrefFor('write', menu.id)) },
            { kind: 'separator' },
            ...planItemMenu(ws, [menu.id], { onRename: (id) => ws.setRenaming(id, 'board'), onCreated: (id) => ws.setRenaming(id, 'board') }),
          ]}
          onClose={() => setMenu(null)}
        />
      ) : null}
      {dating && ws ? (
        <ConfirmDialog
          title="Scheduled for"
          message={
            <label className="schedule-date">
              <span>Publish date</span>
              <input type="date" value={dating.day} onChange={(e) => setDating({ ...dating, day: e.target.value })} />
            </label>
          }
          confirm="Set Date"
          cancel="No Date"
          onCancel={() => setDating(null)}
          onConfirm={() => {
            const [y, m, d] = dating.day.split('-').map(Number);
            if (y && m && d) structureEdits(ws).setField([dating.post.nodeId], 'publish-date', { type: 'date', value: new Date(y, m - 1, d) });
            setDating(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** A post's card: its number and title, synopsis, tags, then when it goes out ("No date" or "Due 9 Oct" in terracotta on
 *  Scheduled), its words, sections and folder. */
function PostCard({ project, post, node, tree, status, href, draggable, onDragStart, onDragEnd, onDropOn, onMenu }: {
  project: Project; post: JournalPostRef; node: TreeNode; tree: TreeNode[]; status: string; href: string; draggable: boolean;
  onDragStart: () => void; onDragEnd: () => void; onDropOn: (side: 'before' | 'after') => void; onMenu: (x: number, y: number) => void;
}) {
  const ws = useWorkspace();
  const [side, setSide] = useState<'before' | 'after' | null>(null);
  const fragment = project.fragment(post.fragmentId);
  const number = JournalPosts.number(fragment?.metadata);
  const date = dateOf(project, post);
  const scheduled = status === JournalPosts.scheduled;
  const today = new Date();
  const overdue = scheduled && !!date && date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const record = (project as Partial<OpenedProject>).fragmentRecord?.(post.fragmentId);
  const tags = record ? fragmentTags(record) : [];
  const sections = post.sections.filter((s) => !pathTo(tree, s.nodeId)?.at(-1)?.isFolder).length;
  const folder = post.parentId ? pathTo(tree, post.parentId)?.at(-1)?.title : null;
  const foot = [formatNumber(project.subtreeWords(node)), sections ? (sections === 1 ? '1 section' : `${sections} sections`) : null, folder || null].filter(Boolean).join(' · ');
  const filtered = ws?.filter?.view && ![post.fragmentId, ...post.sections.map((s) => s.fragmentId)].some((id) => ws.filter!.matchesFragment(id));
  const sideOf = (e: DragEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY - r.top < r.height / 2 ? 'before' : 'after';
  };
  return (
    <li
      className={`card post-card${side ? ` drop-${side}` : ''}${filtered ? ' filtered-out' : ''}`}
      data-node-id={post.nodeId}
      data-post-number={number ?? undefined}
      draggable={draggable}
      onDragStart={(e) => {
        onDragStart();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', node.title || 'Untitled');
      }}
      onDragEnd={() => {
        setSide(null);
        onDragEnd();
      }}
      onDragOver={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.stopPropagation();
        const s = sideOf(e);
        if (s !== side) setSide(s);
      }}
      onDragLeave={() => setSide(null)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const s = sideOf(e);
        setSide(null);
        onDropOn(s);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(e.clientX, e.clientY);
      }}
      onKeyDown={(e) => {
        if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onMenu(r.left + 24, r.top + 32);
          e.preventDefault();
        }
      }}
    >
      <a href={href} draggable={false} onClick={(e) => followLink(e, href)}>
        <span className="card-title">
          {number !== null ? <span className="post-number">#{number}</span> : null}
          <span className="card-title-text">{node.title || 'Untitled'}</span>
        </span>
        {fragment?.synopsis.trim() ? <span className="card-synopsis">{fragment.synopsis}</span> : null}
        {tags.length ? (
          <span className="card-tags tag-chips">
            {tags.map((t) => <TagChip key={t} tag={t} />)}
          </span>
        ) : null}
        <span className="card-foot">
          <span className={overdue || (scheduled && !date) ? 'post-when late' : 'post-when'} data-post-when>
            {date ? (overdue ? `Due ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : dayName(date)) : scheduled ? 'No date' : ''}
          </span>
          <span className="card-words">{foot}</span>
        </span>
      </a>
    </li>
  );
}
